import { useRef, useState } from "react";
import { Volume2, VolumeX, MoveHorizontal } from "lucide-react";
import { Reveal } from "./Reveal";

const VIDEOS = [
  { src: "/assets/videos/web-1.mp4", poster: "/assets/videos/poster-1.jpg", name: "Gabriela Macedo", age: "51 anos" },
  { src: "/assets/videos/web-2.mp4", poster: "/assets/videos/poster-2.jpg", name: "Maria José", age: "60 anos" },
  { src: "/assets/videos/web-3.mp4", poster: "/assets/videos/poster-3.jpg", name: "Suely Martins", age: "60 anos" },
  { src: "/assets/videos/web-4.mp4", poster: "/assets/videos/poster-4.jpg", name: "Suely", age: "60 anos" },
  { src: "/assets/videos/web-5.mp4", poster: "/assets/videos/poster-5.jpg", name: "Maria Clara", age: "" },
];

const VideoCard = ({ video, id, suppressClick }) => {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    if (suppressClick.current) return;
    if (!ref.current) return;
    ref.current.muted = !muted;
    if (muted) ref.current.play().catch(() => {});
    setMuted(!muted);
  };

  return (
    <div
      data-testid={`video-testimonial-${id}`}
      className="group relative w-[230px] shrink-0 snap-start transition-[transform] duration-500 hover:-translate-y-2 sm:w-[250px]"
    >
      <div className="relative overflow-hidden rounded-[1.8rem] border-2 border-champagne/40 shadow-[0_18px_50px_-24px_rgba(0,0,0,0.7)] transition-[border-color,box-shadow] duration-500 group-hover:border-champagne group-hover:shadow-[0_28px_70px_-24px_rgba(127,163,204,0.4)]">
        <video
          ref={ref}
          src={video.src}
          poster={video.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onClick={toggleSound}
          draggable={false}
          className="aspect-[9/16] w-full cursor-pointer object-cover"
        />
        <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-black/55 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-white backdrop-blur-sm">
          Depoimento real
        </span>
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent px-4 pb-4 pt-12">
          <p className="font-display text-base font-semibold tracking-tight text-white">
            {video.name}
            {video.age && (
              <span className="ml-2 font-sans text-xs font-medium text-white/70">
                {video.age}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={toggleSound}
          data-testid={`video-sound-toggle-${id}`}
          aria-label={muted ? "Ativar som" : "Silenciar"}
          className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-transform duration-300 hover:scale-110"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export const Testimonials = () => {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, scrollLeft: 0, moved: false });
  const suppressClick = useRef(false);

  const onDown = (e) => {
    drag.current = { down: true, startX: e.pageX, scrollLeft: trackRef.current.scrollLeft, moved: false };
  };
  const onMove = (e) => {
    if (!drag.current.down) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 6) drag.current.moved = true;
    trackRef.current.scrollLeft = drag.current.scrollLeft - dx;
  };
  const onUp = () => {
    suppressClick.current = drag.current.moved;
    drag.current.down = false;
    setTimeout(() => (suppressClick.current = false), 50);
  };

  return (
    <section id="depoimentos" data-testid="testimonials-section" className="overflow-hidden bg-cream py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-champagne-dark">
            Depoimentos
          </p>
          <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl lg:text-5xl">
            O que dizem os <em className="italic text-champagne-dark">meus clientes</em>
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-stone-400">
            Clientes reais contando, em vídeo, como foi encontrar o plano ideal com
            atendimento personalizado.
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/10 bg-surface px-4 py-2 text-xs font-semibold text-stone-400">
            <MoveHorizontal className="h-4 w-4 text-champagne-dark" />
            Arraste para o lado e veja mais depoimentos
          </p>
        </Reveal>
      </div>

      <div className="mt-14" data-testid="testimonials-carousel">
        <div className="edge-mask">
          <div
            ref={trackRef}
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            className="no-scrollbar flex cursor-grab select-none gap-6 overflow-x-auto px-6 pb-4 pt-2 active:cursor-grabbing sm:px-10 lg:px-[max(2.5rem,calc((100vw-80rem)/2+2rem))]"
          >
            {VIDEOS.map((v, i) => (
              <VideoCard key={v.src} video={v} id={i} suppressClick={suppressClick} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
